import {Component, Inject, OnInit, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {AdvancedSearchUtils} from '@application/helper/advanced-search-utils';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {OrderCarpet} from '@domain/planning-prototype/carpet/order-carpet';
import {ProductionOrder} from '@domain/planning-prototype/carpet/production-order';
import {PlanningOrderLite} from '@domain/planning-prototype/generic-order.interface';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {ProductionOrderPlastic} from '@domain/planning-prototype/plastic/production-order-plastic';
import {ProductionOrderToPlan} from '@domain/planning-prototype/to-plan/production-order-to-plan';
import {ProductionOrderTuft} from '@domain/planning-prototype/tufting/production-order-tuft';
import {ProductionOrderWeaving} from '@domain/planning-prototype/weaving/production-order-weaving';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {Permission} from '@domain/profile/permission.enum';
import {PropertyValue} from '@domain/property-value';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {HttpPlasticProductionOrdersService} from '@infrastructure/http/plastic-production-order/http-plastic-production-orders.service';
import {PRODUCTION_ORDERS_WEAVING, ProductionOrdersWeaving} from '@infrastructure/http/production-order-weaving/production-orders-weaving';
import {PRODUCTION_SCHEDULES, ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {HttpTuftProductionOrdersService} from '@infrastructure/http/tuft-production-order/http-tuft-production-orders.service';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {AdvancedSearchInput} from '@presentation/components/search-filters/advanced-search/advanced-search-input.enum';
import {AdvancedSearchDialogComponent} from '@presentation/components/search-filters/advanced-search/dialog/advanced-search-dialog.component';
import {
  AlertDialogResult,
  AssertionUtils,
  BaseComponent,
  DateTimeFormat,
  DateTimePipe,
  DialogBuilderFactoryService,
  DialogType,
  EnumUtils,
  FilterComponent,
  ObjectActionType,
  StringUtils,
  ToastHelperService,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {BehaviorSubject, debounceTime, distinctUntilChanged, filter, finalize, lastValueFrom, takeUntil} from 'rxjs';
import {PlanningItemDragDropService} from '../../utilities/planning-item-drag-drop.service';
import {PlanningPrototypeContextService} from '../../utilities/planning-prototype-context.service';
import {ProductionOrderUtilities} from '../../utilities/production-order-utilities.service';

@Component({
  selector: 'app-to-plan',
  templateUrl: './to-plan.component.html',
  styleUrls: ['./to-plan.component.scss']
})
export class ToPlanComponent extends BaseComponent implements OnInit {
  @ViewChild('nameFilter') public nameFilter: FilterComponent;

  public filteredOrdersToPlan: ProductionOrderToPlan[];
  public ordersToPlan: ProductionOrderToPlan[];
  public productionOrderIdsOnPlanningDraft: number[] = [];
  public activeSearchFilters: PropertyValue[] = [];
  public nameFilterChanged = new BehaviorSubject<string>(null);
  public draggedItem: ProductionOrderToPlan;
  public advancedSearchFilters: PropertyValue[] = [
    {propertyName: AdvancedSearchInput.NAME},
    {
      propertyName: AdvancedSearchInput.PRODUCTION_SCHEDULE_STATUS,
      possiblePropertyValues: [ProductionScheduleStatus.TO_PLAN, ProductionScheduleStatus.CREATING, ProductionScheduleStatus.RELEASED, ProductionScheduleStatus.TO_RECONFIRM]
    },
    {propertyName: AdvancedSearchInput.MACHINE_QUALITY},
    {propertyName: AdvancedSearchInput.CREEL},
    {propertyName: AdvancedSearchInput.WEAVE_PRODUCT},
    {propertyName: AdvancedSearchInput.TUFT_PRODUCT},
    {propertyName: AdvancedSearchInput.PLASTIC_PRODUCT},
    {propertyName: AdvancedSearchInput.TOOL},
    {propertyName: AdvancedSearchInput.CUSTOMER}
  ];

  public get dragActive(): boolean {
    return this.planningDragDrop.dragActive;
  }

  private isReconfirming = false;

  public constructor(
    private readonly planningContext: PlanningPrototypeContextService,
    private readonly translate: TranslateService,
    private readonly dialogBuilder: DialogBuilderFactoryService,
    @Inject(PRODUCTION_SCHEDULES) private readonly productionSchedules: ProductionSchedules,
    @Inject(PRODUCTION_ORDERS_WEAVING) private readonly productionOrdersWeaving: ProductionOrdersWeaving,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly productionOrdersTufting: HttpTuftProductionOrdersService,
    private readonly productionOrdersPlastic: HttpPlasticProductionOrdersService,
    private readonly productionOrderUtilities: ProductionOrderUtilities,
    private readonly planningDragDrop: PlanningItemDragDropService,
    private readonly toastHelper: ToastHelperService,
    private readonly viewContainer: ViewContainerRef,
    private readonly dateTimePipe: DateTimePipe
  ) {
    super();
  }

  public ngOnInit(): void {
    this.filterAdvancedSearchFields();
    this.filterOrdersToPlanByActivePlanning();
    this.ordersToPlan.sort(this.compareProductionOrder);
    this.subscribeToNameFilterChanges();
    this.refilterOrdersOnPLanningLineChanges();
  }

  public closeMachineDetail(): void {
    this.planningContext.selectItem(null);
  }

  public isToReconfirm(order: ProductionOrderToPlan): boolean {
    return (
      order.productionOrder instanceof ProductionOrder &&
      order.productionOrder.status === ProductionScheduleStatus.TO_RECONFIRM &&
      this.productionOrderIdsOnPlanningDraft.includes(order.productionOrder.id)
    );
  }

  public getProductString(order: ProductionOrderToPlan): string {
    if (order.productionOrder instanceof ProductionOrder) {
      return `${order.productionOrder.quality.name}/${order.productionOrder.creel.name}`;
    }

    if (order.productionOrder instanceof ProductionOrderWeaving) {
      return order.productionOrder.weaveProduct.name;
    }

    if (order.productionOrder instanceof ProductionOrderTuft) {
      return order.productionOrder.tuftProduct.name;
    }

    if (order.productionOrder instanceof ProductionOrderPlastic) {
      const productName = order.productionOrder.plasticProduct.name;
      let result = productName;

      if (order.productionOrder.tool) {
        result += `/${order.productionOrder.tool.name}`;
      }

      if (order.productionOrder.customer) {
        result += `/${order.productionOrder.customer.name}`;
      }

      return result;
    }
    return '';
  }

  public getRequiredQuantityString(order: ProductionOrderToPlan): string {
    let plannedQuantityString: string;

    if (order.productionOrder instanceof ProductionOrder) {
      plannedQuantityString = order.productionOrder.plannedQuantityInPicks + ' ' + this.translate.instant('GENERAL.UNIT.PICKS');
    } else if (order.productionOrder instanceof ProductionOrderPlastic) {
      plannedQuantityString = order.productionOrder.plannedQuantity + ' ' + this.translate.instant(`GENERAL.UNIT.${order.productionOrder.plasticProduct.unitOfMeasurement}`);
    } else {
      plannedQuantityString = order.productionOrder.plannedQuantityInMeters + ' ' + this.translate.instant('GENERAL.UNIT.METER');
    }

    if (AssertionUtils.isNullOrUndefined(order.productionOrder.dueDate)) {
      return plannedQuantityString;
    }
    const dueDate = this.translate.instant('PLANNING.NEEDS_ATTENTION.DUE_AT', {date: this.dateTimePipe.transform(order.productionOrder.dueDate, DateTimeFormat.DATE_SHORT)});
    return `${dueDate} - ${plannedQuantityString}`;
  }

  public showAdvancedSearch(): void {
    this.dialogBuilder
      .getBuilder()
      .withClass(['reposition-dialog', 'needs-attention-advanced-search'])
      .withWidth('650px')
      .withMaxHeight('100%')
      .openDialog(RepositionDialogComponent, {
        advancedSearchFilters: this.advancedSearchFilters,
        component: AdvancedSearchDialogComponent,
        sourceElement: this.nameFilter.iconElement.nativeElement,
        positionOfDialog: PositionOfDialog.BOTTOM
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((filters: PropertyValue[]) => {
        if (AssertionUtils.isEmpty(filters)) {
          return;
        }
        this.advancedSearchFilters = filters;
        this.updateFilter();
      });
  }

  public getChipLabel = (filterProperty: PropertyValue): string => {
    const propertyString = this.translate.instant(`GENERAL.ADVANCED_SEARCH.${EnumUtils.getKeyFromValue(AdvancedSearchInput, filterProperty.propertyName)}`, {count: 1});
    const valueString = AdvancedSearchUtils.getDisplayValueForAdvancedSearchFilter(filterProperty.propertyName, filterProperty.propertyValue, this.translate, Unit.CENTIMETER);
    return `${propertyString} (${valueString})`;
  };

  public removeFilter(filterProperty: PropertyValue | string): void {
    if (typeof filterProperty === 'string') {
      filterProperty = this.activeSearchFilters.find((property: PropertyValue) => this.getChipLabel(property) === filterProperty);
      if (AssertionUtils.isNullOrUndefined(filterProperty)) {
        return;
      }
    }
    filterProperty.propertyValue = undefined;
    this.updateFilter();
  }

  public async deleteUnplannedOrder(order: ProductionOrderToPlan): Promise<void> {
    const title = this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object: order.productionOrder.name});
    const message = this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION');

    const dialogResult = await lastValueFrom(
      this.dialogBuilder
        .getBuilder()
        .openAlertDialog({
          titleText: title,
          messageText: message,
          type: DialogType.CONFIRM_DELETE
        })
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
    );
    if (dialogResult !== AlertDialogResult.CONFIRM) {
      return;
    }

    if (order.productionOrder instanceof ProductionOrder) {
      await lastValueFrom(this.productionSchedules.delete(order.productionOrder.id).pipe(takeUntil(this.unSubscribeOnViewDestroy)));
    } else if (order.productionOrder instanceof ProductionOrderWeaving) {
      await lastValueFrom(this.productionOrdersWeaving.delete(order.productionOrder.id).pipe(takeUntil(this.unSubscribeOnViewDestroy)));
    } else if (order.productionOrder instanceof ProductionOrderTuft) {
      await lastValueFrom(this.productionOrdersTufting.delete(order.productionOrder.id).pipe(takeUntil(this.unSubscribeOnViewDestroy)));
    } else if (order.productionOrder instanceof ProductionOrderPlastic) {
      await lastValueFrom(this.productionOrdersPlastic.delete(order.productionOrder.id).pipe(takeUntil(this.unSubscribeOnViewDestroy)));
    }

    this.planningContext.ordersToPlan.splice(this.planningContext.ordersToPlan.indexOf(order), 1);
    this.filterOrdersToPlanByActivePlanning();
    this.ordersToPlan.sort(this.compareProductionOrder);
    this.updateFilter();
    this.toastHelper.showToastForObjectAction(ObjectActionType.DELETE, 'PRODUCTION_ORDER.PRODUCTION_ORDER', order.productionOrder.name);
  }

  public openPlannedItemSidebar(order: ProductionOrderToPlan): void {
    const itemToSelect = this.getPlanningItemForOrder(order);
    this.planningContext.selectItem(itemToSelect);
  }

  public reconfirmOrder(order: ProductionOrderToPlan): void {
    if (!(order.productionOrder instanceof ProductionOrder) || this.isReconfirming) {
      return;
    }
    this.isReconfirming = true;
    this.productionOrderUtilities
      .reconfirmOrder(order.productionOrder)
      .pipe(
        finalize(() => (this.isReconfirming = false)),
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((confirmed: boolean) => confirmed)
      )
      .subscribe(() => {
        order.productionOrder.status = ProductionScheduleStatus.VERIFIED;
        const plannedItem = this.getPlanningItemForOrder(order);
        if (plannedItem instanceof OrderCarpet) {
          plannedItem.productionOrder.status = ProductionScheduleStatus.VERIFIED;
        }

        this.filterOrdersToPlanByActivePlanning();
        this.ordersToPlan.sort(this.compareProductionOrder);
        this.updateFilter();
      });
  }

  public onDragStart(event: DragEvent, order: ProductionOrderToPlan, template: TemplateRef<any>): void {
    this.draggedItem = order;
    this.planningDragDrop.onDragStart(event, order, template, this.viewContainer);
  }

  public getStatus(order: ProductionOrderToPlan): string {
    if ([ProductionScheduleStatus.CREATING, ProductionScheduleStatus.RELEASED].includes(order.productionOrder.status)) {
      return 'PRODUCTION_ORDER.STATUS.' + order.productionOrder.status;
    }

    return 'PLANNING.NEEDS_ATTENTION.TO_PLAN';
  }

  private filterOrdersToPlanByActivePlanning(): void {
    const flatListOfPlanningItems = this.planningContext.getFlatListOfPlanningItems();

    this.productionOrderIdsOnPlanningDraft = flatListOfPlanningItems
      .filter((item: PlanningItem) => item instanceof OrderCarpet && !AssertionUtils.isNullOrUndefined(item.productionOrder))
      .map((item: OrderCarpet) => item.productionOrder.id);

    const productionOrderLiteIdsOnPlanningDraft = flatListOfPlanningItems.filter(PlanningItemHelper.isOrderLiteItem).map((item: PlanningOrderLite) => item.productionOrder.id);

    this.ordersToPlan = this.planningContext.ordersToPlan.filter((order: ProductionOrderToPlan) => {
      if (order.productionOrder instanceof ProductionOrder) {
        return this.isToReconfirm(order) || !this.productionOrderIdsOnPlanningDraft.includes(order.productionOrder.id);
      } else {
        return !productionOrderLiteIdsOnPlanningDraft.includes(order.productionOrder.id);
      }
    });
  }

  private compareProductionOrder = (orderA: ProductionOrderToPlan, orderB: ProductionOrderToPlan): number => {
    const isOrderAToReconfirm = this.isToReconfirm(orderA);
    const isOrderBToReconfirm = this.isToReconfirm(orderB);

    if (isOrderAToReconfirm !== isOrderBToReconfirm) {
      return isOrderAToReconfirm ? -1 : 1;
    }

    const dueDateOfOrderA = orderA.productionOrder.dueDate?.getTime() ?? Infinity;
    const dueDateOfOrderB = orderB.productionOrder.dueDate?.getTime() ?? Infinity;

    if (dueDateOfOrderA !== dueDateOfOrderB) {
      return dueDateOfOrderA - dueDateOfOrderB;
    }

    return StringUtils.stringComparator(orderA.productionOrder.name, orderB.productionOrder.name);
  };

  private passesActiveFilters(order: ProductionOrderToPlan): boolean {
    return this.activeSearchFilters.every((filterProperty: PropertyValue) => this.passesActiveFilter(order, filterProperty));
  }

  private passesActiveFilter(order: ProductionOrderToPlan, filterProperty: PropertyValue): boolean {
    switch (filterProperty.propertyName) {
      case AdvancedSearchInput.NAME:
        return order.productionOrder.name.toLowerCase().includes(filterProperty.propertyValue?.toLowerCase());

      case AdvancedSearchInput.PRODUCTION_SCHEDULE_STATUS:
        return this.getActiveFilterForStatus(order, filterProperty.propertyValue);

      case AdvancedSearchInput.MACHINE_QUALITY:
        return order.productionOrder instanceof ProductionOrder && order.productionOrder.quality.id === filterProperty.propertyValue.id;

      case AdvancedSearchInput.CREEL:
        return order.productionOrder instanceof ProductionOrder && order.productionOrder.creel.id === filterProperty.propertyValue.id;

      case AdvancedSearchInput.WEAVE_PRODUCT:
        return order.productionOrder instanceof ProductionOrderWeaving && order.productionOrder.weaveProduct.id === filterProperty.propertyValue.id;

      case AdvancedSearchInput.TUFT_PRODUCT:
        return order.productionOrder instanceof ProductionOrderTuft && order.productionOrder.tuftProduct.id === filterProperty.propertyValue.id;

      case AdvancedSearchInput.PLASTIC_PRODUCT:
        return order.productionOrder instanceof ProductionOrderPlastic && order.productionOrder.plasticProduct.id === filterProperty.propertyValue.id;

      case AdvancedSearchInput.TOOL:
        return order.productionOrder instanceof ProductionOrderPlastic && order.productionOrder.tool?.id === filterProperty.propertyValue.id;

      case AdvancedSearchInput.CUSTOMER:
        return order.productionOrder instanceof ProductionOrderPlastic && order.productionOrder.customer?.id === filterProperty.propertyValue.id;
    }
  }

  private subscribeToNameFilterChanges(): void {
    this.nameFilterChanged.pipe(takeUntil(this.unSubscribeOnViewDestroy), distinctUntilChanged(), debounceTime(500)).subscribe((name: string) => {
      const filterProperty = this.advancedSearchFilters.find((property: PropertyValue) => property.propertyName === AdvancedSearchInput.NAME);
      if (!AssertionUtils.isNullOrUndefined(filterProperty)) {
        filterProperty.propertyValue = name;
        this.updateFilter();
      }
    });
  }

  private updateFilter(): void {
    const filterProperty = this.advancedSearchFilters.find((property: PropertyValue) => property.propertyName === AdvancedSearchInput.NAME);
    this.nameFilterChanged.next(filterProperty.propertyValue);
    this.activeSearchFilters = this.advancedSearchFilters.filter((property: PropertyValue) => !AssertionUtils.isNullOrUndefined(property.propertyValue) && property.propertyValue !== '');

    this.filteredOrdersToPlan = this.ordersToPlan.filter((order: ProductionOrderToPlan) => this.passesActiveFilters(order));
  }

  private getPlanningItemForOrder(order: ProductionOrderToPlan): PlanningItem {
    const allItemsOnBoard = this.planningContext.getFlatListOfPlanningItems();
    return allItemsOnBoard.find(
      (item: PlanningItem) => PlanningItemHelper.isOrderItem(item) && item.productionOrder?.constructor === order.productionOrder.constructor && item.productionOrder.id === order.productionOrder.id
    );
  }

  private refilterOrdersOnPLanningLineChanges(): void {
    this.planningContext.planningLineChanges.pipe(takeUntil(this.unSubscribeOnViewDestroy), debounceTime(10)).subscribe(() => {
      this.filterOrdersToPlanByActivePlanning();
      this.ordersToPlan.sort(this.compareProductionOrder);
      this.updateFilter();
    });
  }

  private filterAdvancedSearchFields(): void {
    const currentSubscription = this.authentication.getCurrentSubscription();
    const permissionMap = {
      [AdvancedSearchInput.MACHINE_QUALITY]: Permission.TEXSTYLE_MACHINE_QUALITY_VIEW,
      [AdvancedSearchInput.CREEL]: Permission.TEXSTYLE_CREEL_VIEW,
      [AdvancedSearchInput.WEAVE_PRODUCT]: Permission.TEXSTYLE_WEAVE_PRODUCT_VIEW,
      [AdvancedSearchInput.TUFT_PRODUCT]: Permission.TEXSTYLE_TUFT_PRODUCT_VIEW,
      [AdvancedSearchInput.PLASTIC_PRODUCT]: Permission.TEXSTYLE_PLASTIC_PRODUCT_VIEW,
      [AdvancedSearchInput.TOOL]: Permission.TEXSTYLE_PLASTIC_TOOL_VIEW,
      [AdvancedSearchInput.CUSTOMER]: Permission.TEXFAB_CUSTOMER_VIEW
    };
    this.advancedSearchFilters = this.advancedSearchFilters.filter((field: PropertyValue) => {
      return AssertionUtils.isNullOrUndefined(permissionMap[field.propertyName]) || currentSubscription.hasPermission(permissionMap[field.propertyName]);
    });
  }

  private getActiveFilterForStatus(order: ProductionOrderToPlan, propertyValue: ProductionScheduleStatus): boolean {
    if (propertyValue === ProductionScheduleStatus.TO_RECONFIRM) {
      return this.isToReconfirm(order);
    } else if (propertyValue === ProductionScheduleStatus.CREATING) {
      return !this.isToReconfirm(order) && order.productionOrder.status === ProductionScheduleStatus.CREATING;
    } else if (propertyValue === ProductionScheduleStatus.RELEASED) {
      return !this.isToReconfirm(order) && order.productionOrder.status === ProductionScheduleStatus.RELEASED;
    } else {
      return !this.isToReconfirm(order) && order.productionOrder.status !== ProductionScheduleStatus.CREATING && order.productionOrder.status !== ProductionScheduleStatus.RELEASED;
    }
  }
}
